// frontend/src/pages/itemSetupRow.js
//
// Flatten an ItemLocation API response row into a flat object for AG Grid.
//
// The wire row is an ItemLocation (ItemLocationSerializer) carrying planning
// params and the flat item/location names at the top level, plus a nested
// ``item`` (Item wire) and nested ``location``. AG Grid columns address every
// field flat (`field: 'brand'`), so Item fields must be hoisted to the top
// level.
//
// We SPREAD ``il`` and ``il.item`` as the base rather than hand-picking each
// key: a column that reads a field a pick-list forgot (shopify_product_gid,
// sor, …) would silently see `undefined`. Spreading means any Item or
// ItemLocation serializer field a column reads is present by construction;
// below we only remap the handful that are renamed, coerced, or derived.
//
// Pure function (depends only on its argument) — extracted from the page so the
// wire→grid contract can be pinned by unit tests without rendering the grid.

/**
 * @param {object} il  An ItemLocation wire row: planning params at the top
 *   level, plus nested `item` (Item wire) and `location`.
 * @returns {object}  A flat row keyed the way the grid's column `field`s and
 *   the save-payload builder expect.
 */
export function flattenItemLocationRow(il) {
  // The nested `location` is not hoisted: the grid reads the flat
  // `location_code` / `location_name` the serializer sends beside it.
  const { item, location: _location, ...ilFields } = il;
  // Drop the flat FK-id mirrors: each has an authoritative nested twin on the
  // row (the pickers read/write the object; save maps object → PK). Keeping
  // the flat id would let it drift stale after an edit — the setters update the
  // object, not the mirror — and mislead a future reader into trusting it.
  // (The default supplier has no flat id on the wire — default_supplier_id is
  // write-only — so only user_id / product_category_id need dropping.
  // default_supplier_link_id stays: it has no nested twin, and it is the id
  // a supplier SKU edit is saved to — itemSetupFields.js saveTargetOf.)
  const {
    user_id: _uid, product_category_id: _pcid,
    ...itemFields
  } = item;
  return {
    // Base: all ItemLocation-level fields (item_location_id, planning params,
    // classifications), then all Item fields hoisted flat (Item wins on the
    // audit-timestamp keys the two share — irrelevant, none are displayed).
    ...ilFields,
    ...itemFields,

    // Renames / coercions / derived the grid relies on.
    _ItemID: itemFields.item_id,
    // Cost currency per row — Item.supplier_currency_code, derived server-side
    // from ItemLocation → Supplier → DefaultCurrency. Standalone column.
    cost_currency_code: itemFields.supplier_currency_code,
    npi_flag: !!itemFields.npi_flag,           // booleanColumn wants a real boolean
    _promotion: null,                          // merged later from active_promotions
  };
}
